/**
 * PEPulse Aktivitäts-Protokoll — tages-/wochenweise Log-Übersicht (Admin-Cockpit).
 */

import React, { useMemo } from 'react';
import { SocialMediaLog } from '../../store/socialMediaStore';
import { SOCIAL_MEDIA_POINTS, SocialMediaLogType } from '../../utils/socialMediaScoring';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    coachName: string;
    coachEmail: string;
    coachUserId: string;
    month: string;
    logs: SocialMediaLog[];
};

type DayAgg = {
    date: string;
    story: number;
    post: number;
    reel: number;
    points: number;
};

const WEEKDAY_DE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

const TYPE_BADGE: Record<
    SocialMediaLogType,
    { label: string; className: string }
> = {
    story: {
        label: 'Story',
        className: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
    },
    post: {
        label: 'Beitrag',
        className: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
    },
    reel: {
        label: 'Reel',
        className: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
    },
};

/** ISO-KW (Montag-basiert) für YYYY-MM-DD */
const isoWeekNumber = (dateIso: string): number => {
    const m = String(dateIso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return 0;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (Number.isNaN(d.getTime())) return 0;
    const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = tmp.getUTCDay() || 7;
    tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
    return Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};

const formatDayLabel = (dateIso: string): string => {
    const m = String(dateIso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return dateIso || '—';
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const day = Number(m[3]);
    const d = new Date(y, mo - 1, day);
    const wd = WEEKDAY_DE[d.getDay()] || '';
    const kw = isoWeekNumber(dateIso);
    const dd = String(day).padStart(2, '0');
    const mm = String(mo).padStart(2, '0');
    return `KW ${String(kw).padStart(2, '0')} – ${wd}, ${dd}.${mm}.${y}`;
};

const PepulseActivityLogModal: React.FC<Props> = ({
    isOpen,
    onClose,
    coachName,
    coachEmail,
    coachUserId,
    month,
    logs,
}) => {
    const filtered = useMemo(() => {
        const email = String(coachEmail || '').trim().toLowerCase();
        const uid = String(coachUserId || '').trim();
        return (logs || []).filter((l) => {
            if (l.month && l.month !== month) return false;
            const logEmail = String(l.userEmail || '').trim().toLowerCase();
            const logUid = String(l.userId || '').trim();
            if (email && logEmail && logEmail === email) return true;
            if (uid && logUid && logUid === uid) return true;
            return false;
        });
    }, [logs, coachEmail, coachUserId, month]);

    const totals = useMemo(() => {
        let story = 0;
        let post = 0;
        let reel = 0;
        for (const l of filtered) {
            const c = Math.max(1, Number(l.count) || 1);
            if (l.type === 'story') story += c;
            else if (l.type === 'post') post += c;
            else if (l.type === 'reel') reel += c;
        }
        return { story, post, reel };
    }, [filtered]);

    const dayRows = useMemo(() => {
        const byDate: Record<string, DayAgg> = {};
        for (const l of filtered) {
            const date = String(l.date || '').trim();
            if (!date) continue;
            if (!byDate[date]) {
                byDate[date] = { date, story: 0, post: 0, reel: 0, points: 0 };
            }
            const c = Math.max(1, Number(l.count) || 1);
            const pts = Number(l.points);
            const addPts = Number.isFinite(pts) && pts >= 0 ? pts : SOCIAL_MEDIA_POINTS[l.type] * c;
            if (l.type === 'story') byDate[date].story += c;
            else if (l.type === 'post') byDate[date].post += c;
            else if (l.type === 'reel') byDate[date].reel += c;
            byDate[date].points += addPts;
        }
        return Object.values(byDate).sort((a, b) => b.date.localeCompare(a.date));
    }, [filtered]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-0 sm:p-4"
            role="dialog"
            aria-modal="true"
            aria-label="P-Pulse Aktivitäts-Protokoll"
        >
            <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={onClose}
                aria-hidden
            />
            <div className="relative w-full sm:max-w-lg max-h-[88vh] flex flex-col rounded-t-2xl sm:rounded-2xl border border-slate-700 bg-[#08090b] shadow-2xl overflow-hidden">
                <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-slate-800 bg-[#0c0d11]">
                    <div className="min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-[#E6C299]">
                            P-Pulse Aktivitäts-Protokoll
                        </div>
                        <h2 className="text-lg font-black text-white truncate">{coachName || 'Coach'}</h2>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Monat {month} · Total:{' '}
                            <span className="text-slate-300">
                                {totals.story} Stories | {totals.post} Posts | {totals.reel} Reels
                            </span>
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="shrink-0 w-9 h-9 rounded-lg border border-slate-700 bg-slate-900 text-slate-400 hover:text-white hover:border-slate-500 transition-colors text-lg leading-none"
                        aria-label="Schließen"
                    >
                        ×
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                    {dayRows.length === 0 ? (
                        <p className="text-sm text-slate-500 italic py-6 text-center">
                            Keine Aktivitäten für diesen Monat protokolliert
                        </p>
                    ) : (
                        dayRows.map((day) => {
                            const badges: { type: SocialMediaLogType; count: number }[] = [];
                            if (day.story > 0) badges.push({ type: 'story', count: day.story });
                            if (day.post > 0) badges.push({ type: 'post', count: day.post });
                            if (day.reel > 0) badges.push({ type: 'reel', count: day.reel });
                            const suspicious =
                                day.reel >= 3 || day.story >= 8 || day.post >= 4;

                            return (
                                <div
                                    key={day.date}
                                    className={`rounded-xl border px-3 py-2.5 ${
                                        suspicious
                                            ? 'border-amber-500/40 bg-amber-500/5'
                                            : 'border-slate-800 bg-[#0c0d11]'
                                    }`}
                                >
                                    <div className="flex items-center justify-between gap-2 mb-2">
                                        <span className="text-xs font-bold text-slate-300">
                                            {formatDayLabel(day.date)}
                                        </span>
                                        <span className="text-[10px] font-mono text-slate-500">
                                            +{Math.round(day.points)} Pkt
                                        </span>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {badges.map(({ type, count }) => {
                                            const conf = TYPE_BADGE[type];
                                            const pts = SOCIAL_MEDIA_POINTS[type] * count;
                                            return (
                                                <span
                                                    key={type}
                                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-bold ${conf.className}`}
                                                >
                                                    {count}× {conf.label}
                                                    <span className="opacity-70 font-mono">
                                                        (+{pts})
                                                    </span>
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};

export default PepulseActivityLogModal;
